"use client";

import { useId, useRef, type FormEvent } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import clsx from "clsx";
import { ArrowRight } from "lucide-react";
import {
  captureLandingEvent,
  landingAnalyticsEvents,
  normalizeLandingPlan,
} from "@/shared/components/Analytics/landingAnalytics";

function encodeFormData(formData: FormData) {
  const params = new URLSearchParams();

  formData.forEach((value, key) => {
    if (typeof value === "string") {
      params.append(key, value);
    }
  });

  return params.toString();
}

function getStringFormValue(formData: FormData, key: string) {
  const value = formData.get(key);
  return typeof value === "string" ? value.trim() : "";
}

type EarlyAccessFormProps = {
  selectedPlan: "starter" | "pro" | null;
};

export function EarlyAccessForm({ selectedPlan }: Readonly<EarlyAccessFormProps>) {
  const nameId = useId();
  const emailId = useId();
  const websiteId = useId();
  const teamSizeId = useId();
  const notesId = useId();

  const router = useRouter();
  const formStartedRef = useRef(false);
  const fieldClass =
    "mt-1 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-[15px] text-foreground shadow-[0_1px_0_rgba(255,255,255,.8)_inset,0_1px_2px_rgba(15,23,42,.04)] outline-none transition placeholder:text-slate-400 focus:border-blue/50 focus:ring-4 focus:ring-blue/10 dark:border-white/15 dark:bg-white/5";
  const selectClass = clsx(fieldClass, "appearance-none pr-9");

  function getSafeFormAnalyticsProperties(formData?: FormData) {
    return {
      form_name: "early-access",
      selected_plan: formData
        ? normalizeLandingPlan(getStringFormValue(formData, "plan"))
        : selectedPlan,
      team_size: formData ? getStringFormValue(formData, "teamSize") || null : null,
      has_product_url: formData ? Boolean(getStringFormValue(formData, "website")) : null,
      has_notes: formData ? Boolean(getStringFormValue(formData, "notes")) : null,
    };
  }

  function handleFormStarted() {
    if (formStartedRef.current) return;
    formStartedRef.current = true;

    captureLandingEvent(
      landingAnalyticsEvents.formStarted,
      getSafeFormAnalyticsProperties()
    );
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const myForm = event.target as HTMLFormElement;
    const formData = new FormData(myForm);
    const safeAnalyticsProperties = getSafeFormAnalyticsProperties(formData);

    captureLandingEvent(landingAnalyticsEvents.formSubmitted, safeAnalyticsProperties);

    try {
      const response = await fetch("/__early-access-form.html", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encodeFormData(formData),
      });

      if (!response.ok) {
        throw new Error(`Form submission failed with status ${response.status}`);
      }

      captureLandingEvent(landingAnalyticsEvents.formSucceeded, safeAnalyticsProperties);

      const query = window.location.search;
      router.push(`/sign-up/thanks${query}`);
    } catch (error) {
      captureLandingEvent(landingAnalyticsEvents.formFailed, {
        ...safeAnalyticsProperties,
        error_type: error instanceof Error ? error.name : "unknown",
      });
      alert(error instanceof Error ? error.message : String(error));
    }
  };

  return (
    <main className="relative overflow-hidden" data-page="sign-up">
      <section className="relative flex min-h-[calc(100svh-5rem)] items-center py-12 md:min-h-svh md:pb-14 md:pt-32 lg:pb-16 lg:pt-36">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-[34rem] bg-[radial-gradient(circle_at_18%_12%,rgba(37,99,235,.16),transparent_34%),radial-gradient(circle_at_82%_8%,rgba(15,23,42,.08),transparent_32%),linear-gradient(180deg,rgba(239,246,255,.86),rgba(255,255,255,0))]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-24 hidden h-px w-[70rem] -translate-x-1/2 bg-gradient-to-r from-transparent via-blue/25 to-transparent lg:block"
        />
        <div className="relative mx-auto max-w-xl px-6 lg:px-8">
          <div>
            <style>{`
              @keyframes brief-gray-ring { 
                0% { box-shadow: 0 0 0 0 rgba(148,163,184,0), 0 0 0 rgba(148,163,184,0) }
                100% { box-shadow: 0 0 0 7px rgba(148,163,184,.18), 0 0 18px rgba(148,163,184,.12) }
              }
              @media (prefers-reduced-motion: reduce) { .brief-ring-anim { animation: none !important } }
            `}</style>
            <div className="relative">
              <div
                aria-hidden
                className="pointer-events-none absolute -inset-1 rounded-[2rem] brief-ring-anim"
                style={{ animation: "brief-gray-ring 2.2s ease-in-out infinite alternate" }}
              />
              <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_28px_90px_rgba(15,23,42,.14)] backdrop-blur-sm dark:border-white/10 dark:bg-neutral-900/80">
                <div className="border-b border-slate-200 bg-slate-50/80 px-5 py-5 sm:px-6">
                  <p className="text-sm font-semibold uppercase tracking-[0.22em] text-blue">
                    Sign up
                  </p>
                  <h1 className="mt-2 text-2xl font-bold tracking-tight text-foreground">
                    Let Lucien run your go-to-market.
                  </h1>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    Start free for one week. No credit card required.
                  </p>
                  <p className="mt-3 rounded-2xl border border-blue/10 bg-blue/[0.04] px-3.5 py-3 text-sm leading-6 text-slate-700">
                    Share the clearest context you have. Lucien uses it to prepare your first
                    buyer signal brief and one action to approve.
                  </p>
                </div>
                <form
                  name="early-access"
                  data-netlify="true"
                  netlify-honeypot="steak"
                  onSubmit={handleSubmit}
                  onFocusCapture={handleFormStarted}
                  className="p-5 sm:p-6"
                >
                <input type="hidden" name="form-name" value="early-access" />
                <input type="hidden" name="plan" value={selectedPlan ?? ""} />
                <p className="hidden">
                  <label>
                    Ignore if you’re human: <input name="steak" />
                  </label>
                </p>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-1">
                    <label htmlFor={nameId} className="block text-sm font-medium text-foreground">
                      Name
                    </label>
                    <input
                      id={nameId}
                      name="name"
                      type="text"
                      required
                      autoComplete="name"
                      className={fieldClass}
                      placeholder="Ada Lovelace"
                      minLength={3}
                    />
                  </div>

                  <div className="sm:col-span-1">
                    <label htmlFor={emailId} className="block text-sm font-medium text-foreground">
                      Email
                    </label>
                    <input
                      id={emailId}
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      className={fieldClass}
                      placeholder="you@company.com"
                    />
                  </div>

                  <div className="sm:col-span-1">
                    <label
                      htmlFor={websiteId}
                      className="block text-sm font-medium text-foreground"
                    >
                      Website or product URL{" "}
                      <span className="text-foreground/50">(optional)</span>
                    </label>
                    <input
                      id={websiteId}
                      name="website"
                      type="url"
                      inputMode="url"
                      className={fieldClass}
                      placeholder="https://yourproduct.com"
                    />
                  </div>

                  <div className="sm:col-span-1">
                    <label
                      htmlFor={teamSizeId}
                      className="block text-sm font-medium text-foreground"
                    >
                      Team size
                    </label>
                    <div className="relative">
                      <select
                        id={teamSizeId}
                        name="teamSize"
                        required
                        defaultValue=""
                        className={selectClass}
                      >
                        <option value="" disabled>
                          Select...
                        </option>
                        <option value="solo">Solo</option>
                        <option value="2-5">2–5</option>
                        <option value="6-15">6–15</option>
                        <option value="16-50">16–50</option>
                        <option value="51+">51+</option>
                      </select>
                      <svg
                        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/70"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z" />
                      </svg>
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor={notesId} className="block text-sm font-medium text-foreground">
                      Notes <span className="text-foreground/50">(optional)</span>
                    </label>
                    <textarea
                      id={notesId}
                      name="notes"
                      rows={4}
                      className={fieldClass}
                      placeholder="What have you built? Who do you think the buyer is? What part of GTM feels stuck right now?"
                    />
                  </div>
                </div>

                <div className="mt-6 flex flex-col-reverse items-stretch justify-between gap-3 sm:flex-row sm:items-center">
                  <p className="text-xs text-muted-foreground">
                    By submitting, you agree to our {""}
                    <Link href="/legal/privacy" className="underline">
                      Privacy Policy
                    </Link>
                    .
                  </p>
                  <button
                    type="submit"
                    data-cta="early-access-submit"
                    className={clsx(
                      "inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-blue px-5 text-sm font-semibold text-white shadow-sm transition disabled:opacity-60 disabled:cursor-not-allowed hover:brightness-110 active:brightness-95 sm:h-11",
                      "ring-4 ring-transparent hero-cta-animated [animation:hero-cta-ring_1.8s_ease-in-out_infinite_alternate]"
                    )}
                  >
                    Start free
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
